import React, { useEffect, useRef, useState } from 'react';

export interface ArtSelectOption {
    value: number | string;
    label: string;
}

interface Props {
    options: ArtSelectOption[];
    /** Current value. `null` means nothing selected — placeholder is shown. */
    value: number | string | null;
    onChange: (value: number | string | null) => void;
    /** Label shown when value is null. Also the label of the "clear" item if allowClear is on. */
    placeholder?: string;
    /** When true, an extra item at the top of the list deselects (sends null). */
    allowClear?: boolean;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
    /** `sm` for compact contexts (pagination), `md` (default) for form fields. */
    size?: 'sm' | 'md';
    /** Optional: error state for form integration. */
    hasError?: boolean;
    onBlur?: () => void;
}

// Max-height of the dropdown panel (see articles.scss: 242px). Used to decide
// whether to open downward or flip upward when room below is tight.
const DROPDOWN_MAX_HEIGHT = 250;

const ArtSelect: React.FC<Props> = ({
    options,
    value,
    onChange,
    placeholder = '—',
    allowClear = false,
    disabled = false,
    ariaLabel,
    className = '',
    size = 'md',
    hasError = false,
    onBlur,
}) => {
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const openDropdown = () => {
        // Compute placement just before opening so the panel never gets clipped
        // by the viewport bottom. Flips upward when there's more room above.
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setPlacement(spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow ? 'top' : 'bottom');
        }
        setOpen(true);
    };

    const toggle = () => {
        if (disabled) return;
        if (open) setOpen(false);
        else openDropdown();
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                onBlur?.();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, onBlur]);

    const selected = options.find((o) => o.value === value);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Escape' && open) {
            setOpen(false);
            triggerRef.current?.focus();
        }
    };

    const pick = (next: number | string | null) => {
        onChange(next);
        setOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div
            className={`art-select ${size === 'sm' ? 'art-select--sm' : ''} ${hasError ? 'has-error' : ''} ${className}`}
            ref={containerRef}
        >
            <button
                ref={triggerRef}
                type="button"
                className={`art-select__trigger ${open ? 'is-open' : ''}`}
                onClick={toggle}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
            >
                <span className={`art-select__value ${!selected ? 'is-placeholder' : ''}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <svg className="art-select__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div className={`art-select__dropdown art-select__dropdown--${placement}`} role="listbox">
                    {allowClear && (
                        <button
                            type="button"
                            role="option"
                            aria-selected={value === null}
                            className={`art-select__option ${value === null ? 'is-selected' : ''}`}
                            onClick={() => pick(null)}
                        >
                            <span className="art-select__option-label">{placeholder}</span>
                        </button>
                    )}
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={opt.value === value}
                            className={`art-select__option ${opt.value === value ? 'is-selected' : ''}`}
                            onClick={() => pick(opt.value)}
                        >
                            <span className="art-select__option-label">{opt.label}</span>
                            {opt.value === value && (
                                <svg className="art-select__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ArtSelect;
